"use client"

import { useState, useEffect, useMemo, useRef } from 'react'
import { Banknote, Square, Printer, CheckCircle2, ArrowRight, AlertTriangle, Wallet, HandCoins, KeyRound } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { useStore } from '@/lib/store'
import { billPriced } from '@/lib/tables'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import { printZReport, showCreditLines } from '@/lib/print'
import { useFeature } from '@/hooks/use-feature'
import { BANK_CODES, CARD_PROVIDER_LABELS, type BankCode, type Shift } from '@/lib/mock-data'
import { drawerChain, returnsSplit, shiftPreview, shownSplit } from '@/lib/cash'
import { TAKEOUT_LABELS, bigCountGap, closeSplit, lastBank, movementNote, openingGap, salaryHintFor, salaryPaidToday, takenAtClose, type TakeoutReason } from '@/lib/drawer-float'
import { businessDay } from '@/lib/business-day'
import { exchangesByShift } from '@/lib/exchange'
import { movementText, takeoutText } from '@/lib/drawer-text'
import { clockText, numDateText, useLang, useT } from '@/lib/i18n'
import { BankMark } from '@/components/ui/bank-mark'

interface Props {
  open: boolean
  onClose: () => void
  shift: Shift | null
}

// Sized for a till read across the counter: SAMGORI's cashiers (2026-10-03)
// found the closing screen too small. `big` is for the three numbers the
// count is about — expected, counted, difference.
function Row({ label, value, strong, big, color }: { label: string; value: string; strong?: boolean; big?: boolean; color?: string }) {
  return (
    <div className="flex justify-between items-center gap-3 text-base">
      <span className={cn('text-muted-foreground', (strong || big) && 'font-bold text-foreground', big && 'text-lg')}>{label}</span>
      <span className={cn('tabular-nums', big ? 'text-3xl font-black' : strong ? 'text-xl font-black' : 'text-lg font-bold', color ?? 'text-foreground')}>{value}</span>
    </div>
  )
}

export function CloseShiftDialog({ open, onClose, shift: liveShift }: Props) {
  const t = useT()
  const lang = useLang()
  const { sales, closeShift, customerPayments, supplierPayments, cashMovements, expenses, settings, offlinePending, isSyncingOffline, salaryCeilingHint, shifts } = useStore()
  // 1.39 — the returns split and the expected-cash sum wait for the history (below)
  const historyReady = useStore((st) => st.historyReady)
  // 1.66 (192) — the café's open bills in this shift's branch: shown, never counted (they are paid when the guest pays)
  const allBills = useStore((st) => st.bills)
  const tableProducts = useStore((st) => st.products)
  const tablePromos = useStore((st) => st.promotions)
  const openBills = useMemo(() => Object.values(allBills ?? {}).filter((b) => b.branch_id === liveShift?.branch_id), [allBills, liveShift?.branch_id])
  const openBillsSum = useMemo(() => openBills.reduce((s, b) => s + billPriced(b, tableProducts, tablePromos).total, 0), [openBills, tableProducts, tablePromos])
  const [countedCash, setCountedCash] = useState('')
  // 119 — what stays in the drawer for the next shift, and where the rest
  // goes. The field follows the owner's float until someone types in it.
  const [leftStr, setLeftStr] = useState('')
  const [leftTouched, setLeftTouched] = useState(false)
  const [reason, setReason] = useState<TakeoutReason>('owner')
  const [note, setNote] = useState('')
  const [confirmTakeAll, setConfirmTakeAll] = useState(false)
  const [bank, setBank] = useState<BankCode | null>(null)   // 120
  // 137 — the cashier's salary: proposed from Settings until someone types
  const [salaryStr, setSalaryStr] = useState('')
  const [salaryTouched, setSalaryTouched] = useState(false)
  const [pinOpen, setPinOpen] = useState(false)
  const [pinTyped, setPinTyped] = useState('')
  // 1.19 — a count far off the expected asks once before closing (bigCountGap)
  const [gapOk, setGapOk] = useState(false)
  const [gapOpen, setGapOpen] = useState(false)
  const countRef = useRef<HTMLInputElement>(null)
  const fixingCount = useRef(false)
  const [saving, setSaving] = useState(false)
  const [report, setReport] = useState<Shift | null>(null)
  const creditOn = useFeature('credit')
  // The shift this dialog is closing stays its own after the close. The
  // parent passes the store's OPEN shift, which turns null the moment
  // close_shift succeeds — and returning null below then unmounted the
  // Z-report it had just produced: the toast said „Z-რეპორტი მზადაა" over an
  // empty screen, and its print button was gone (found by the smoke robot,
  // 2026-09-26).
  const [held, setHeld] = useState<Shift | null>(liveShift)
  useEffect(() => { if (liveShift) setHeld(liveShift) }, [liveShift])
  const shift = liveShift ?? (report ? held : null)

  useEffect(() => {
    if (!open) return
    setCountedCash(''); setReport(null)
    setLeftStr(''); setLeftTouched(false); setReason('owner'); setNote(''); setConfirmTakeAll(false)
    setSalaryStr(''); setSalaryTouched(false); setPinTyped('')
    setGapOk(false); setGapOpen(false)
    // the bank named last is offered first — a shop mostly uses one
    const st = useStore.getState()
    setBank(lastBank(st.shifts, st.cashMovements))
  }, [open])

  // Live preview of this shift. The cash arithmetic is NOT written here: it
  // comes from `lib/cash.ts`, which the accounting page also calls and which
  // `lib/cash.test.ts` pins to `close_shift()` in migration 010. This used to
  // be a hand-written second copy of that formula. It happened to agree — and
  // a third copy on the accounting page did not, which is how a paid ნისია
  // came to be invisible in „სალაროში შემოსული".
  // 107 — money put in or taken out during the shift, oldest first: listed
  // under their totals here and on the Z-report, so the owner reads who took
  // what and why, not only a sum.
  const moves = useMemo(
    () => (shift ? cashMovements.filter(m => m.shift_id === shift.id).sort((a, b) => a.created_at.localeCompare(b.created_at)) : []),
    [cashMovements, shift],
  )

  // 1.36 — exchanges of this shift, counted from its sale rows (they never change)
  const exchanges = useMemo(() => (shift ? exchangesByShift(sales).get(shift.id) : undefined), [sales, shift])
  // 1.39 — where this shift's returns went back (SAMGORI2026's call: a card
  // refund in „დაბრუნებები" while the expected cash took off only the cash part)
  const returnRails = useMemo(() => (shift ? returnsSplit(sales.filter(s => s.shift_id === shift.id)) : undefined), [sales, shift])

  // 1.41 — one rule for this dialog and the shift menu (`shiftPreview`, lib/cash.ts)
  // 176 — the gift cards and deposits of the shift: their cash is in the drawer
  const prepaidMoves = useStore((s) => s.prepaidMoves)
  const preview = useMemo(
    () => (shift ? shiftPreview(shift, { sales, customerPayments, supplierPayments, cashMovements: moves, prepaidMoves }) : null),
    [sales, shift, customerPayments, supplierPayments, moves, prepaidMoves],
  )

  if (!shift) return null

  const counted = Math.max(0, parseFloat(countedCash) || 0)
  const diff = report ? report.difference : counted - (preview?.expected ?? 0)

  // 119 — the owner's float, proposed; less than it left is reminded once.
  const floatSetting = { keep: settings.floatKeep, amount: settings.floatAmount }
  // 137 — the salary comes out of the count first; the float and „taken"
  // follow. One tested rule (lib/drawer-float.ts closeSplit), not arithmetic here.
  // 143 — what THIS CASHIER was already paid today, in any shift (close_shift
  // holds the same rule); a cashier login without expenses gets the server's hint.
  const paidSalary = salaryPaidToday(shift.cashier_id, businessDay(new Date(), settings.dayStartHour), shifts, cashMovements, expenses)
  const { showSalary, ceiling, salary, afterSalary, proposedLeft: proposed, left, take, remind } = closeSplit({
    counted,
    salarySetting: { keep: settings.salaryKeep, amount: settings.salaryAmount },
    paidSalary,
    // the ceiling the server named (SALARY_LIMIT:<n>) wins — Review Focus 2
    hint: salaryHintFor(salaryCeilingHint, shift.id, businessDay(new Date(), settings.dayStartHour)),
    salaryTyped: salaryTouched ? salaryStr : null,
    floatSetting,
    leftTyped: leftTouched ? leftStr : null,
  })
  const gapAtOpen = openingGap(shift)

  // `gapAccepted` is passed by „კი, სწორია": its setGapOk(true) is not yet
  // in this render's closure. Every later press reads the state.
  const handleClose = async (gapAccepted = gapOk) => {
    // The count first: everything below it (salary, float, „taken") is
    // derived from it, so a typo there is fixed before anything else.
    if (!gapAccepted && preview && bigCountGap(counted, preview.expected)) { setGapOpen(true); return }
    if (salary > counted + 0.005) { toast.error(t('close.err.salaryOver')); return }
    if (left > afterSalary + 0.005) { toast.error(t('close.err.floatOver')); return }
    if (take > 0 && reason === 'other' && !note.trim()) { toast.error(t('close.err.noteRequired')); return }
    if (take > 0 && reason === 'bank' && !bank) { toast.error(t('close.err.pickBank')); return }
    // A reminder, never a refusal: the second press closes (owner, 2026-09-27).
    if (remind && !confirmTakeAll) { setConfirmTakeAll(true); return }
    // 137 — above the ceiling the owner's money PIN goes with the close
    if (salary > ceiling + 0.005 && !pinTyped) { setPinOpen(true); return }
    setSaving(true)
    const closed = await closeShift(shift.id, counted, {
      float: left,
      reason: take > 0 ? reason : null,
      note: take > 0 && note.trim() ? note.trim() : null,
      bank: take > 0 && reason === 'bank' ? bank : null,
      salary,
      overridePin: salary > ceiling + 0.005 ? pinTyped : null,
    })
    setSaving(false)
    if (closed) { setReport(closed); toast.success(t('close.done')) }
    else setPinTyped('')
  }

  // The Z-report document lives in lib/print.ts: it is printed from here, from
  // the shift history on /staff, and from the POS when the till is closed —
  // because a shop with no printer attached at closing time still has to be
  // able to produce it afterwards, and a cashier cannot reach /staff at all.
  const printZ = () => {
    if (!report) return
    if (!printZReport(report, settings, moves, { credit: creditOn, exchanges, returns: returnRails })) {
      toast.error(t('common.popupBlocked'))
    }
  }

  const r = report // authoritative when present, else preview
  // 176 — what a gift card or a deposit paid is the third rail of the sales
  const ppSales = r ? (r.prepaid_sales ?? 0) : (preview?.prepaidSales ?? 0)
  const total = (r ? r.cash_sales + r.card_sales + r.credit_sales + ppSales - r.returns_total
                   : (preview ? preview.cash + preview.card + preview.credit + ppSales - preview.returns : 0))
  // shown only when it adds up to the shift's own returns total (lib/cash.ts),
  // and for an open shift only once the history is in: before that the preview
  // and the split come from the same partial rows and would agree on a wrong sum
  // (Codex, 1.39 review). The frozen report is checked against the server's total.
  const rs = r || historyReady ? shownSplit(returnRails, r ? r.returns_total : (preview?.returns ?? 0)) : null
  // 168 — the change rounding: the frozen shift's own columns, else the preview
  const keptIn = r ? (r.change_kept_in ?? 0) : preview!.keptIn
  const keptOut = r ? (r.change_kept_out ?? 0) : preview!.keptOut
  // 176 — money taken for a prepaid, and handed back: never turnover; the cash two
  // are terms of the expected cash, the card two are for the terminal's day
  const ppInCash = r ? (r.prepaid_in_cash ?? 0) : preview!.prepaidInCash
  const ppOutCash = r ? (r.prepaid_out_cash ?? 0) : preview!.prepaidOutCash
  const ppInCard = r ? (r.prepaid_in_card ?? 0) : preview!.prepaidInCard
  const ppOutCard = r ? (r.prepaid_out_card ?? 0) : preview!.prepaidOutCard
  const cardSales = r ? r.card_sales : preview!.card
  // 1.39 — the expected cash written out term by term; null when it does not add up
  const chain = drawerChain({
    opening: shift.opening_cash,
    cashSales: r ? r.cash_sales : preview!.cash,
    creditPaid: r ? r.credit_paid : preview!.creditPaid,
    debtPayments: r ? r.customer_payments_cash : preview!.custCash,
    cashRefunds: rs ? rs.cash : 0,
    exchangeCash: rs ? rs.exchangeCash : 0,
    supplierCash: r ? r.supplier_payments_cash : preview!.suppCash,
    drawerIn: r ? r.cash_in_total : preview!.drawerIn,
    drawerOut: r ? r.cash_out_total : preview!.drawerOut,
    keptIn,
    keptOut,
    // 176 — cash taken for a gift card or a deposit, and handed back
    prepaidIn: ppInCash,
    prepaidOut: ppOutCash,
  }, r ? r.expected_cash : preview!.expected)
  const showChain = !!chain && chain.length > 1 && (!!r || historyReady)
  // the card's returns of the shift: 0 with none, the split's card part when it adds up, else unknown
  const cardBack = (r ? r.returns_total : preview?.returns ?? 0) < 0.005 ? 0 : rs ? rs.card : null

  return (
    <>
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      {/* Only the body between the title and the buttons scrolls (2026-10-07).
          The footer used to be sticky inside one scrolling dialog, and a field
          brought into view on focus — by the phone when its keyboard came up,
          by our touch keyboard — went to the bottom edge of that scroller,
          right under the footer: the count was typed blind (the owner's phone
          and a narrow till window, both 0 px of the field in sight). With the
          footer outside the scroller, the scroller's edge IS the footer's top. */}
      <DialogContent className="sm:max-w-xl rounded-[24px] p-6 sm:p-7 flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-2xl font-black flex items-center gap-2.5">
            {report ? <><CheckCircle2 className="size-6 text-emerald-500" /> {t('close.zTitle')}</> : <><Square className="size-6 text-amber-500" /> {t('shift.close')}</>}
          </DialogTitle>
          <DialogDescription className="text-base">
            {shift.cashier_name} · {t('close.openedAt', { date: numDateText(shift.opened_at, { year: false, time: true }, lang) })}
          </DialogDescription>
        </DialogHeader>

        <div id="receipt-print" className="flex flex-col gap-3 py-2 min-h-0 flex-1 overflow-y-auto">
          {!report && openBills.length > 0 && (
            <div className="rounded-2xl border border-border bg-card px-4 py-3">
              <p className="text-sm font-bold">{t('tables.openAtClose', { n: openBills.length, sum: openBillsSum.toFixed(2) })}</p>
              <p className="text-[12px] text-muted-foreground">{t('tables.openAtCloseHint')}</p>
            </div>
          )}
          <div className="bg-muted/40 rounded-2xl p-4 sm:p-5 flex flex-col gap-2">
            <Row label={t('close.opening')} value={`₾${shift.opening_cash.toFixed(2)}`} />
            {/* 119 — the float typed at opening against what the previous
                shift left: money that vanished between two shifts shows. */}
            {gapAtOpen !== null && gapAtOpen !== 0 && (
              <Row
                label={t('close.openingGap', { sum: `₾${(shift.opening_carried ?? 0).toFixed(2)}` })}
                value={`${gapAtOpen > 0 ? '+' : '−'}₾${Math.abs(gapAtOpen).toFixed(2)}`}
                color={gapAtOpen < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}
              />
            )}
            <Row label={t('close.cashSales')} value={`₾${(r ? r.cash_sales : preview!.cash).toFixed(2)}`} />
            <Row label={t('pay.byCard')} value={`₾${cardSales.toFixed(2)}`} />
            {ppSales >= 0.005 && <Row label={t('close.prepaidSales')} value={`₾${ppSales.toFixed(2)}`} />}
            {showCreditLines(creditOn, r ? r.credit_sales : preview!.credit, r ? r.credit_paid : preview!.creditPaid) && (
              <>
                <Row label={t('close.creditSales')} value={`₾${(r ? r.credit_sales : preview!.credit).toFixed(2)}`} />
                <Row label={t('close.creditPaid')} value={`₾${(r ? r.credit_paid : preview!.creditPaid).toFixed(2)}`} />
              </>
            )}
            {(r ? r.returns_total : preview!.returns) > 0 && (
              <Row label={t('close.returns')} value={`−₾${(r ? r.returns_total : preview!.returns).toFixed(2)}`} color="text-red-600 dark:text-red-400" />
            )}
            {/* 1.39 — which part went back from the drawer, which on a card, which off a debt */}
            {rs && (
              <ul className="flex flex-col gap-0.5 -mt-0.5 pl-3 border-l-2 border-border">
                {([['close.returnsCash', rs.cash], ['close.returnsCard', rs.card], ['close.returnsPrepaid', rs.prepaid], ['close.returnsCredit', rs.credit], ['close.returnsExchange', rs.exchange]] as const)
                  .filter(([, v]) => v >= 0.005)
                  .map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-3 text-sm text-muted-foreground">
                      <span className="truncate">{t(k)}</span>
                      <span className="tabular-nums shrink-0">−₾{v.toFixed(2)}</span>
                    </li>
                  ))}
              </ul>
            )}
            {exchanges && exchanges.count > 0 && (
              <Row label={t('close.exchanges', { n: exchanges.count, sum: `₾${exchanges.extra.toFixed(2)}` })} value="" />
            )}
            <Separator className="my-1 opacity-50" />
            <Row label={t('close.salesTotal')} value={`₾${total.toFixed(2)}`} strong />
            <Row label={t('close.salesCount')} value={String(r ? r.sales_count : preview!.count)} />
          </div>

          {/* Cash reconciliation */}
          <div className="bg-primary/5 rounded-2xl p-4 sm:p-5 flex flex-col gap-3 border border-primary/10">
            {(r ? r.customer_payments_cash : preview!.custCash) > 0 && (
              <Row label={t('close.debtPayments')} value={`+₾${(r ? r.customer_payments_cash : preview!.custCash).toFixed(2)}`} color="text-emerald-600 dark:text-emerald-400" />
            )}
            {(r ? r.supplier_payments_cash : preview!.suppCash) > 0 && (
              <Row label={t('close.supplierPayments')} value={`−₾${(r ? r.supplier_payments_cash : preview!.suppCash).toFixed(2)}`} color="text-red-600 dark:text-red-400" />
            )}
            {(r ? r.cash_in_total : preview!.drawerIn) > 0 && (
              <Row label={t('close.drawerIn')} value={`+₾${(r ? r.cash_in_total : preview!.drawerIn).toFixed(2)}`} color="text-emerald-600 dark:text-emerald-400" />
            )}
            {(r ? r.cash_out_total : preview!.drawerOut) > 0 && (
              <Row label={t('close.drawerOut')} value={`−₾${(r ? r.cash_out_total : preview!.drawerOut).toFixed(2)}`} color="text-red-600 dark:text-red-400" />
            )}
            {ppInCash >= 0.005 && (
              <Row label={t('close.prepaidInCash')} value={`+₾${ppInCash.toFixed(2)}`} color="text-emerald-600 dark:text-emerald-400" />
            )}
            {ppOutCash >= 0.005 && (
              <Row label={t('close.prepaidOutCash')} value={`−₾${ppOutCash.toFixed(2)}`} color="text-amber-700 dark:text-amber-400" />
            )}
            {(ppInCard >= 0.005 || ppOutCard >= 0.005) && (
              <p className="-mt-1 text-[13px] leading-relaxed text-muted-foreground tabular-nums">
                {t('close.prepaidCard', { in: `₾${ppInCard.toFixed(2)}`, out: `₾${ppOutCard.toFixed(2)}` })}
                {/* the terminal's day, NET — only when the card returns are known (Codex, final review #5) */}
                {cardBack !== null && (
                  <><br />{t('close.terminalDay', {
                    sum: `₾${(cardSales - cardBack + ppInCard - ppOutCard).toFixed(2)}`, card: `₾${cardSales.toFixed(2)}`,
                    back: `₾${cardBack.toFixed(2)}`, pp: `₾${ppInCard.toFixed(2)}`, out: `₾${ppOutCard.toFixed(2)}`,
                  })}</>
                )}
              </p>
            )}
            {/* 168 — „ხურდის თეთრები": kept raises the drawer, given lowers it. Shown
                whenever there is any, whatever the switch says now. */}
            {(keptIn >= 0.005 || keptOut >= 0.005) && (
              <div className="flex flex-col gap-0.5">
                <Row
                  label={t('close.changeKept')}
                  value={`${keptIn - keptOut >= 0 ? '+' : '−'}₾${Math.abs(keptIn - keptOut).toFixed(2)}`}
                  color={keptIn - keptOut >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}
                />
                <p className="text-[13px] text-muted-foreground tabular-nums">
                  {t('close.changeKeptSplit', { kept: `₾${keptIn.toFixed(2)}`, given: `₾${keptOut.toFixed(2)}` })}
                </p>
              </div>
            )}
            {moves.length > 0 && (
              <ul className="flex flex-col gap-0.5 -mt-0.5 pl-3 border-l-2 border-border">
                {moves.map(m => (
                  <li key={m.id} className="flex justify-between gap-3 text-sm text-muted-foreground">
                    <span className="truncate">
                      {clockText(lang, m.created_at)} · {movementText(t, m)}{movementNote(m) ? ` — ${movementNote(m)}` : ''}
                    </span>
                    <span className="tabular-nums shrink-0">{m.direction === 'in' ? '+' : '−'}₾{m.amount.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            )}
            <Row label={t('close.expected')} value={`₾${(r ? r.expected_cash : preview!.expected).toFixed(2)}`} big color="text-primary" />
            {/* 1.39 — how the expected cash was reached (SAMGORI2026's call: a
                card refund looked like it should have left the drawer) */}
            {showChain && chain && (
              <p className="-mt-2 text-[13px] leading-relaxed text-muted-foreground tabular-nums">
                {chain.map((x, i) => (
                  <span key={x.key}>
                    {i === 0 ? '' : x.sign > 0 ? ' + ' : ' − '}
                    <span className="whitespace-nowrap">₾{x.amount.toFixed(2)} {t(`close.chain.${x.key}`)}</span>
                  </span>
                ))}
                <span className="whitespace-nowrap">{' = '}<b className="text-foreground">₾{(r ? r.expected_cash : preview!.expected).toFixed(2)}</b></span>
              </p>
            )}
            {!report ? (
              <div className="flex flex-col gap-2">
                <label htmlFor="close-counted" className="text-base font-bold text-foreground">{t('close.countedLabel')} <span className="text-red-500">*</span></label>
                <div className="relative">
                  <Banknote className="absolute left-4 top-1/2 -translate-y-1/2 size-6 text-muted-foreground" />
                  <Input ref={countRef} id="close-counted" type="number" min="0" inputMode="decimal" autoFocus className="h-16 pl-14 rounded-2xl bg-card dark:bg-white/5 text-3xl md:text-3xl font-black tabular-nums" placeholder="0.00" value={countedCash} onChange={e => { setCountedCash(e.target.value); setConfirmTakeAll(false); setGapOk(false) }} />
                </div>
              </div>
            ) : (
              <Row label={t('close.counted')} value={`₾${(report.closing_cash ?? 0).toFixed(2)}`} big />
            )}
            <Separator className="my-0.5 opacity-50" />
            <Row
              label={t('close.diff')}
              value={`${diff > 0 ? '+' : ''}₾${diff.toFixed(2)}`}
              big
              color={Math.abs(diff) < 0.005 ? 'text-emerald-600' : diff > 0 ? 'text-emerald-600' : 'text-red-600'}
            />
            {report && Math.abs(diff) >= 0.005 && (
              <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2">
                {diff > 0 ? t('close.over') : t('close.short')}
              </p>
            )}
            {report && report.closing_float != null && (
              <>
                <Separator className="my-0.5 opacity-50" />
                {(report.closing_salary ?? 0) > 0 && (
                  <Row label={t('close.salary', { name: report.cashier_name ?? '—' })} value={`−₾${(report.closing_salary ?? 0).toFixed(2)}`} color="text-red-600 dark:text-red-400" />
                )}
                <Row label={t('close.leftForNext')} value={`₾${report.closing_float.toFixed(2)}`} strong />
                {takenAtClose(report) > 0 && (
                  <Row
                    label={`${t('close.taken', { where: takeoutText(t, report.takeout_reason, report.takeout_bank) })}${report.takeout_note ? ` · ${report.takeout_note}` : ''}`}
                    value={`−₾${takenAtClose(report).toFixed(2)}`}
                    color="text-red-600 dark:text-red-400"
                  />
                )}
              </>
            )}
          </div>

          {/* 119 — after the count: what stays for the next shift, and where
              the rest goes. The count and its difference above are final;
              this only says what happens to the money next. */}
          {!report && countedCash !== '' && (
            <div className="rounded-2xl border border-border p-4 sm:p-5 flex flex-col gap-3">
              {showSalary && (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="close-salary" className="text-base font-bold text-foreground">
                    {t('close.salaryLabel', { name: shift.cashier_name ?? '—' })}
                  </label>
                  <div className="relative">
                    <HandCoins className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                    <Input
                      id="close-salary" type="text" inputMode="decimal" autoComplete="off"
                      className="h-14 pl-12 rounded-2xl bg-card dark:bg-white/5 text-2xl md:text-2xl font-black tabular-nums"
                      value={salaryTouched ? salaryStr : String(salary)}
                      onChange={e => { setSalaryTouched(true); setSalaryStr(e.target.value); setConfirmTakeAll(false) }}
                    />
                  </div>
                  {paidSalary > 0 && <p className="text-sm text-muted-foreground">{t('close.salaryPaid', { sum: `₾${paidSalary.toFixed(2)}` })}</p>}
                  {salary > ceiling + 0.005 && <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">{t('close.salaryOver', { sum: `₾${ceiling.toFixed(2)}` })}</p>}
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="close-float" className="text-base font-bold text-foreground">{t('close.floatLabel')}</label>
                {floatSetting.keep && floatSetting.amount > 0 && (
                  <span className="text-sm text-muted-foreground shrink-0">{t('close.floatSetting', { sum: `₾${floatSetting.amount.toFixed(2)}` })}</span>
                )}
              </div>
              <div className="relative">
                <Wallet className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                <Input
                  id="close-float" type="number" min="0" inputMode="decimal"
                  className="h-14 pl-12 rounded-2xl bg-card dark:bg-white/5 text-2xl md:text-2xl font-black tabular-nums"
                  value={leftTouched ? leftStr : String(proposed)}
                  onChange={e => { setLeftTouched(true); setLeftStr(e.target.value); setConfirmTakeAll(false) }}
                />
              </div>
              {take > 0 && (
                <>
                  <div className="flex items-center justify-between text-base">
                    <span className="text-muted-foreground">{t('close.takingOut')}</span>
                    <span className="text-xl font-black tabular-nums text-foreground">₾{take.toFixed(2)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={t('close.whereAria')}>
                    {(Object.keys(TAKEOUT_LABELS) as TakeoutReason[]).map(k => (
                      <button
                        key={k} type="button" role="radio" aria-checked={reason === k}
                        onClick={() => setReason(k)}
                        className={cn('h-12 rounded-xl border text-sm font-bold transition-colors',
                          reason === k ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted/60')}
                      >
                        {t(`takeout.${k}` as const)}
                      </button>
                    ))}
                  </div>
                  {/* 120 — which bank: a statement is matched by it. Kept
                      apart from takings by bank (the card column). */}
                  {reason === 'bank' && (
                    <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label={t('bank.which')}>
                      {BANK_CODES.map(b => (
                        <button
                          key={b} type="button" role="radio" aria-checked={bank === b}
                          onClick={() => setBank(b)}
                          className={cn('h-12 rounded-xl border text-sm font-bold transition-colors',
                            bank === b ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted/60')}
                        >
                          <span className="inline-flex items-center justify-center gap-1.5"><BankMark bank={b} className="size-4" />{CARD_PROVIDER_LABELS[b]}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {reason === 'other' && (
                    <Input
                      className="h-12 rounded-xl text-base md:text-base" maxLength={200}
                      placeholder={t('close.notePh')}
                      value={note} onChange={e => setNote(e.target.value)}
                    />
                  )}
                </>
              )}
              {remind && (
                <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/10 border border-amber-500/25 p-3">
                  <AlertTriangle className="size-5 shrink-0 text-amber-600 dark:text-amber-500 mt-0.5" />
                  <p className="text-sm leading-relaxed text-amber-900 dark:text-amber-200">
                    <span className="font-black">{t('close.remind', { sum: `₾${floatSetting.amount.toFixed(2)}` })}</span>{' '}
                    {confirmTakeAll ? t('close.remindConfirm') : t('close.remindLeft', { sum: `₾${left.toFixed(2)}` })}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sales still waiting in the offline queue are part of THIS shift
            but not yet in the database: close_shift would not count them,
            and they would land in a closed shift afterwards — a Z-report and
            a ledger that disagree (audit, 2026-09-11). */}
        {offlinePending > 0 && !report && (
          <p className="shrink-0 text-sm font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 rounded-xl px-3 py-2">
            {t('close.offlinePending', { n: offlinePending })}
          </p>
        )}

        {/* Always at the dialog's bottom edge, below the scrolling body: with
            the bigger type the filled-in form is taller than a 1080 p till,
            and „დახურვა" must not be below the fold. On a phone the two
            buttons share one row (as the product dialog's do) — stacked they
            took 153 px of the little a keyboard leaves. */}
        <DialogFooter className="shrink-0 gap-2 sm:gap-0 max-sm:flex-row -mx-6 -mb-6 sm:-mx-7 sm:-mb-7 px-6 sm:px-7 bg-popover rounded-b-[24px]">
          {!report ? (
            <>
              <Button variant="ghost" className="rounded-xl h-14 px-6 text-base font-bold text-muted-foreground" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
              <Button onClick={() => void handleClose()} disabled={saving || countedCash === '' || offlinePending > 0 || isSyncingOffline} className="rounded-xl h-14 px-8 max-sm:flex-1 text-lg bg-amber-500 hover:bg-amber-600 text-white border-0 font-black">
                {confirmTakeAll && remind ? t('close.yesClose') : t('common.close')} <ArrowRight className="size-5 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="rounded-xl h-14 px-6 text-base font-bold" onClick={printZ}>
                <Printer className="size-5 mr-1.5" /> {t('common.print')}
              </Button>
              <Button onClick={onClose} className="rounded-xl h-14 px-8 max-sm:flex-1 text-lg bg-emerald-600 hover:bg-emerald-700 text-white border-0 font-black">
                {t('close.finish')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

      {/* 1.19 — the count is far off the expected: a typo (SAMGORI 09-29 typed
          5337 for 533.70) or a real gap. Asked once, never refused. „შევასწორებ"
          puts the cursor back in the count, its digits selected. */}
      <AlertDialog open={gapOpen} onOpenChange={setGapOpen}>
        <AlertDialogContent
          className="rounded-2xl md:data-[size=default]:max-w-md"
          finalFocus={() => {
            if (!fixingCount.current) return true
            fixingCount.current = false
            countRef.current?.select()
            return countRef.current
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-xl font-black">
              <AlertTriangle className="size-6 text-amber-500" /> {t('close.gapTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {t(diff > 0 ? 'close.gapOver' : 'close.gapUnder', { sum: `₾${Math.abs(diff).toFixed(2)}` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="rounded-xl bg-muted/40 p-4 flex flex-col gap-2">
            <Row label={t('close.counted')} value={`₾${counted.toFixed(2)}`} big />
            <Row label={t('close.expected')} value={`₾${(preview?.expected ?? 0).toFixed(2)}`} />
            <Row
              label={t('close.diff')}
              value={`${diff > 0 ? '+' : '−'}₾${Math.abs(diff).toFixed(2)}`}
              strong
              color={diff > 0 ? 'text-emerald-600' : 'text-red-600'}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-xl h-12 px-5 text-base font-bold"
              onClick={() => { fixingCount.current = true }}
            >
              {t('close.gapFix')}
            </AlertDialogCancel>
            <Button
              className="rounded-xl h-12 px-6 text-base font-black bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => { setGapOk(true); setGapOpen(false); void handleClose(true) }}
            >
              {t('close.gapYes')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 137 — a salary above the ceiling needs the owner's money PIN. The
          comparison is the SERVER's (close_shift): this device may not hold the
          value, and a wrong PIN comes back { refused } and is counted (115). */}
      <AlertDialog open={pinOpen} onOpenChange={setPinOpen}>
        <AlertDialogContent className="rounded-2xl sm:max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><KeyRound className="size-5 text-amber-500" /> {t('close.pinTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('close.pinBody', { sum: `₾${salary.toFixed(2)}`, limit: `₾${ceiling.toFixed(2)}` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form
            onSubmit={e => { e.preventDefault(); if (!pinTyped) return; setPinOpen(false); void handleClose() }}
            className="flex flex-col gap-3"
          >
            <input
              type="password" inputMode="numeric" autoComplete="new-password" name="no-autofill-close-pin" autoFocus maxLength={8}
              value={pinTyped}
              onChange={e => setPinTyped(e.target.value.replace(/\D/g, ''))}
              className="h-14 w-full rounded-xl border-2 border-border bg-card text-center text-2xl font-black tracking-[0.5em] outline-none focus:border-amber-400"
              placeholder="••••"
            />
            <AlertDialogFooter>
              <AlertDialogCancel type="button" className="rounded-xl" onClick={() => setPinTyped('')}>{t('common.cancel')}</AlertDialogCancel>
              <Button type="submit" className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white">{t('common.confirm')}</Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
